import { Fragment } from 'react';

import {
  Button,
  Flex,
  Link,
  reactIcons,
  Typography,
} from '@project44/manifest-ds';

import { scopedT } from '../../utils/functions/scopedT';
import background from './background.png';

import {
  buttonContainerStyle,
  buttonStyle,
  infoContainerStyles,
  infoStyles,
  layoutStyles,
  pageScreenStyles,
} from './styles';

interface ListItem {
  title: string;
  description: string;
}

export interface UpsellConfig {
  icon?: string;
  title: string;
  description: string;
  loginUrl?: string;
  tourUrl: string;
  learnMoreUrl: string;
  screenshot: string;
  video?: string;
  videoSubtitles?: string;
  list: ListItem[];
}

const t = scopedT();

export const Upsell = (config: UpsellConfig) => {
  return (
    // Main page container
    <Flex
      align="center"
      css={{
        background: `black url(${background})`,
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'cover',
        width: '100%',
        minHeight: '100vh',
        overflowY: 'auto',
        overflowX: 'hidden',
        '@media(min-width: 2300px)': {
          justifyContent: 'center',
        },
      }}
    >
      {/* top container */}
      <Flex css={layoutStyles}>
        {/* left side */}
        <Flex css={infoContainerStyles}>
          <Flex orientation="vertical" justify="center" css={infoStyles}>
            {/* head */}
            <Flex orientation="vertical" justify="center" gap="medium">
              {config.icon && (
                <img
                  src={config.icon}
                  style={{ width: '64px', height: '64px' }}
                  alt={config.title}
                />
              )}
              <Typography variant="display">{config.title}</Typography>
              <Typography variant="title">{config.description}</Typography>
            </Flex>
            {/* list */}
            <Flex orientation="vertical" gap="large">
              {config.list.map((item) => (
                <Flex
                  key={item.title}
                  gap="small"
                  align="start"
                  orientation="vertical"
                >
                  <Typography variant="bodyBold">{item.title}</Typography>
                  {item.description}
                </Flex>
              ))}
            </Flex>
            {/* controls */}
            <Flex orientation="vertical" gap="large" align="center">
              <Flex
                orientation="horizontal"
                justify="between"
                css={buttonContainerStyle}
              >
                <Button
                  variant="secondary"
                  size="medium"
                  css={buttonStyle}
                  onPress={() => window.location.replace(config.tourUrl)}
                  endIcon={<reactIcons.Grow />}
                >
                  <Typography variant="bodyBold">
                    {t('upsellPage.takeTourBtn')}
                  </Typography>
                </Button>
                <Button
                  variant="secondary"
                  size="medium"
                  onPress={() => window.open(config.learnMoreUrl, '_blank')}
                  css={buttonStyle}
                  endIcon={<reactIcons.OpenInNewTab />}
                >
                  <Typography variant="bodyBold">
                    {t('upsellPage.learnMoreBtn')}
                  </Typography>
                </Button>
              </Flex>
              {config.loginUrl && (
                <Link
                  href={config.loginUrl}
                  target="_blank"
                  css={{
                    color: '$palette-grey-300',
                    cursor: 'pointer',
                  }}
                >
                  {t('upsellPage.logInBtn')}
                </Link>
              )}
            </Flex>
          </Flex>
        </Flex>
        {/* right side */}
        <Flex css={pageScreenStyles}>
          {config.video ? (
            <Fragment key={config.video}>
              <video
                autoPlay
                muted
                controls
                disablePictureInPicture
                crossOrigin="anonymous"
              >
                <source src={config.video} type="video/mp4" />
                {config.videoSubtitles && (
                  <track
                    kind="subtitles"
                    srcLang="en"
                    src={config.videoSubtitles}
                    default
                  />
                )}
              </video>
            </Fragment>
          ) : (
            <img src={config.screenshot} alt={config.title} />
          )}
        </Flex>
      </Flex>
    </Flex>
  );
};
